import { TimeSlotType } from '../bookings';
import { Gender } from '../general';
import { AgreementType, LegalAgreement } from '../legal';
import { Allergens, NutritionRating } from '../nutrition';
import { AccountType, ICurrentPlanDetails, SupportedCurrency } from '../payments';
import { getPropNames } from '../reflection';
import { Equipment, IWorkoutImportConfig, StrengthRatioForMuscleGroups } from '../workout';
import { StripeSubscriptionStatus } from '../wrapper';
import { IInfluencerSettings } from './influencer-settings';
import { ISignupSource } from './ISignupSource';
import { IUserPaymentDetail } from './IUserPaymentDetail';
import { Level } from './level';
import { KnownMediums } from './medium';
import { IUserDataForMemberManagement } from './Member';
import { ProgramType } from './ProgramType';
import { ISpecialOffer } from './SpecialOffer';

export const PALValues = {
  NotActive: 1.4,
  ModeratelyActive: 1.6,
  HighlyActive: 1.8,
};

export interface IFamilyPlanSettings {
  pin?: string;
  profileColor?: string;
  hasPin?: boolean;
}

export interface IHeartRateMonitors {
  studio?: string;
  home?: string;
}

export enum LeadStatus {
  Warm = 'Warm',
  Cold = 'Cold',
  Won = 'Won',
  Lost = 'Lost',
}

export interface IPlanOwnerStatus {
  currentPlanStatus: StripeSubscriptionStatus;
}

export interface IExternalUserReference {
  /**
   * unique id for a ClassPass user
   * generated by ClassPass, passed when booking made
   */
  classPassUserId?: string;
}

export interface IStripeSubscriptionData {
  accountBalance?: number;
  contractEndDate?: number;
  contractStartDate?: number;
  extendedContractInDays?: number;
  isContract: boolean;
  hasShedule: boolean;
  subscriptionStartsAt?: number;
  subscriptionCancelsAt?: number;
  delinquent: boolean | null;
  paymentAmount: number | null;
  paymentStatus: string | null;
  paymentId: string | null;
  planDescription: string | null;
  discountApplied: number | null;
  discountCodeApplied: string | null;
  discountEnd: number | null;
}

export function propNamesIUserNames() { return getPropNames<IUserNames>();}
export interface IUserNames {
  uid: string;
  fullname: string;
  firstname: string;
  lastname: string;
  nickname?: string;
}

export class BaseUserProfile implements IUserPaymentDetail, IPlanOwnerStatus, IUserDataForMemberManagement, IUserNames {
  public static maxLengths = { bio: 250 };
  public static getAge(dateOfBirth: Date) {
    const now = new Date();
    let age = now.getFullYear() - dateOfBirth.getFullYear();
    const monthDifference = now.getMonth() - dateOfBirth.getMonth();
    if (monthDifference < 0 || (monthDifference === 0 && now.getDate() < dateOfBirth.getDate())) {
      age--;
    }
    return age;
  }

  public static isMinor(dateOfBirth: string): boolean {
    const age = BaseUserProfile.getAge(new Date(dateOfBirth));
    return age < 18;
  }

  public static isUnderAge(dateOfBirth: string): boolean {
    const age = BaseUserProfile.getAge(new Date(dateOfBirth));
    return age < 16;
  }

  public static isInfluencer(userProfile: BaseUserProfile): boolean {
    return !!userProfile.isStreamingUser;
  }

  public static isHomeAccountType(user: BaseUserProfile): boolean {
    return user.accountType === AccountType.Home;
  }

  public static hasNeverCheckedIn(user: BaseUserProfile): boolean {
    return !user.enrolledFaceId;
  }

  public static createMedicalAgreement(): LegalAgreement {
    return LegalAgreement.createSignedInstance(AgreementType.Medical);
  }

  public static buildFullname(userProfile: BaseUserProfile): string {
    return `${userProfile.firstname} ${userProfile.lastname}`;
  }

  public static hasValidWeight(weight: number): boolean {
    if (typeof weight === 'number') {
      const minWeight = 20;
      const maxWeight = 300;
      return weight > minWeight && weight < maxWeight;
    }
    return false;
  }

  public static hasUserCompletedTerminalSignup(userProfile: BaseUserProfile): boolean {
    return !!userProfile.continueSignupToken;
  }

  public static isNewUserThatHasntHadSubscriptionYet(userProfile: BaseUserProfile): boolean {
    return !userProfile.currentPlanStatus;
  }

  public static isFeatureEnabledForUser = (
    releaseType: 'alpha' | 'beta',
    userProfile: BaseUserProfile,
    additionalCondition: () => boolean = () => true): boolean => {
    const isCompanyUser = userProfile.email.indexOf('@nreality.com') > 0;
    const { has24HourAccess } = userProfile;

    switch (releaseType) {
      case 'alpha':
        return isCompanyUser && additionalCondition();
      case 'beta':
        return (isCompanyUser || has24HourAccess) && additionalCondition();
      default:
        return isCompanyUser && additionalCondition();
    }
  }

  public static hasActivePlan(userProfile: BaseUserProfile): boolean {
    const myPlanIsActive =
      !!userProfile.currentPlanId &&
      this.subscriptionStatusIsActive(userProfile.currentPlanStatus);
    const planOwnerIsActive = !!userProfile.planOwnerStatus &&
      this.subscriptionStatusIsActive(userProfile.planOwnerStatus.currentPlanStatus);
    return myPlanIsActive || planOwnerIsActive;
  }

  public static subscriptionStatusIsActive(status: StripeSubscriptionStatus): boolean {
    return status === 'active' ||
           status === 'unpaid' ||
           status === 'trialing' ||
           status === 'incomplete' ||
           status === 'past_due';
  }

  public static calculateHeartRateMax(age: number): number {
    const ageMultiplier = 0.67;
    const baseHeartRate = 206.9;
    const estimatedHeartRate = baseHeartRate - (ageMultiplier * age);
    return estimatedHeartRate;
  }

  public static isPartOfFamilyPlan(userProfile: BaseUserProfile): boolean {
    return (userProfile.planOwnerUserProfileId !== undefined) || userProfile.isFamilyPlanAdmin;
  }

  public static isPartOfSameFamilyPlan(currentUser: BaseUserProfile, userToSwitchTo: BaseUserProfile): boolean {
    if (currentUser.isFamilyPlanAdmin) {
      return currentUser.uid === userToSwitchTo.planOwnerUserProfileId;
    }

    if (userToSwitchTo.isFamilyPlanAdmin) {
      return userToSwitchTo.uid === currentUser.planOwnerUserProfileId;
    }

    if (currentUser.planOwnerUserProfileId && userToSwitchTo.planOwnerUserProfileId) {
      return userToSwitchTo.planOwnerUserProfileId === currentUser.planOwnerUserProfileId;
    }

    return false;
  }

  public static getUserInitials(userProfile: BaseUserProfile): string {
    return userProfile.firstname.slice(0, 2).toUpperCase();
  }

  public static getProfileColor(userProfile: BaseUserProfile): string | undefined {
    return userProfile.familyPlanSettings && userProfile.familyPlanSettings.profileColor;
  }

  public static isProfileProtectedByPin(user: BaseUserProfile): boolean {
    return !!(user.familyPlanSettings && user.familyPlanSettings.pin);
  }

  public static planExpiresAt(userStripeSubscriptionData: IStripeSubscriptionData): number | undefined {
    return userStripeSubscriptionData?.contractEndDate
      || userStripeSubscriptionData?.subscriptionCancelsAt;
  }

  public static userHasCompletedOnboarding(user: BaseUserProfile): boolean {
    return !!(user.longTermGoal && user.skillLevel);
  }

  public uid: string;
  public stripeUserId: string;
  public externalReference?: IExternalUserReference;
  public email: string;
  public fullname: string;
  public hasUsedExtendedFreeTrail: boolean;
  public enrolledFaceId: string;
  public planHistory: string[];
  public currentPlanExpiresAt: number | undefined;
  public currentPlanId: string | undefined;
  public currentPlanCancelAtEndOfTerm: boolean;
  public currentPlanStatus: StripeSubscriptionStatus;
  public stripeSubscriptionData: IStripeSubscriptionData;
  public timeSlotType: TimeSlotType;
  /**
   * identifies sponsor of the user's access, eg employer might pay for gym access
   */
  public companyId?: string;
  public employeeId?: string; // used for recon purposes
  public isFamilyPlanAdmin: boolean = false;
  public isFamilyPlanMember?: boolean;
  public planOwnerUserProfileId?: string;
  public dateOfBirth: string;
  public firstname: string;
  public lastname: string;
  public branch: string;
  public coupon?: string;
  public continueSignupToken: string | undefined;
  public continueSignupLink?: string;
  public heartRateMax: number;
  public has24HourAccess: boolean;
  public mobileNumber: string;
  public nutritionRatings: NutritionRating[];
  public accountType: AccountType;
  public gender: Gender;
  public longTermGoal: ProgramType;
  public skillLevel: Level;
  public useImperialUnit?: boolean;
  public PALValue: number;
  public useLegacyWorkoutSelectionForHome?: boolean;
  public familyPlanSettings?: IFamilyPlanSettings;
  public planOwnerStatus?: IPlanOwnerStatus;
  public isStreamingUser?: boolean;
  public defaultEquipment?: Equipment[];
  public influencerSettings?: IInfluencerSettings;
  public bio?: string;
  public strengthRatioForMuscleGroups?: StrengthRatioForMuscleGroups;
  public userSignupDate: string;
  public ptApprovedAt: number;
  /**
   * @deprecated Use signupSource.medium instead
   */
  public medium?: KnownMediums;
  public signupSource?: ISignupSource;
  public gymName?: string;
  public leadStatus?: LeadStatus;
  public currency?: SupportedCurrency;
  public specialOffers?: ISpecialOffer[];
  public allergens?: Allergens[];
  public isHighlyActive?: boolean;
  public followUpDate?: Date;
  public teams?: string[];
  public heartRateMonitors?: IHeartRateMonitors;
  public WorkoutImportConfig?: IWorkoutImportConfig[];

  constructor(uid?: string, initData?: Partial<BaseUserProfile>) {
    if (!initData) {
      return;
    }
    Object.assign(this, initData);
    this.uid = uid || '';
    this.setHeartRateMax();
  }

  public applyParentPlanDetails(planOwnerUserProfile: BaseUserProfile) {
    this.planOwnerStatus = {
      currentPlanStatus: planOwnerUserProfile.currentPlanStatus,
    };

    if (!this.planOwnerStatus ||
      !BaseUserProfile.subscriptionStatusIsActive(this.planOwnerStatus.currentPlanStatus)) {
      return;
    }
  }

  public setAgeFromDateOfBirth() {
    if (this.dateOfBirth != null) {
      const dateOfBirth = new Date(this.dateOfBirth);
      (this as any).age = BaseUserProfile.getAge(dateOfBirth);
    }
  }

  // Don't clear current plan id, as we use it to determine the stripe account
  public clearCurrentPlanAndTimeSlotType(inactivePlanId: string | undefined) {
    this.setSubscriptionDetails({
      timeSlotType: TimeSlotType.None,
      currentPlanId: inactivePlanId,
      currentPlanExpiresAt: undefined,
      currentPlanStatus: 'canceled',
      currentPlanCancelAtEndOfTerm: this.currentPlanCancelAtEndOfTerm,
      isFamilyPlanAdmin: this.isFamilyPlanAdmin,
      stripeSubscriptionData: undefined,
    });
  }

  public setSubscriptionDetails(subscription: ICurrentPlanDetails) {
    const rebuiltObjectToForceOnlyCurrentPlanDetailFields: ICurrentPlanDetails = {
      currentPlanId: subscription.currentPlanId,
      currentPlanStatus: subscription.currentPlanStatus,
      currentPlanExpiresAt: subscription.currentPlanExpiresAt,
      currentPlanCancelAtEndOfTerm: subscription.currentPlanCancelAtEndOfTerm,
      timeSlotType: subscription.timeSlotType,
      isFamilyPlanAdmin: subscription.isFamilyPlanAdmin,
      stripeSubscriptionData: subscription.stripeSubscriptionData,
    };
    Object.assign(this, rebuiltObjectToForceOnlyCurrentPlanDetailFields);
  }

  public setHeartRateMax() {
    if (!this.dateOfBirth) {
      this.heartRateMax = 0;
    }

    const dateOfBirth = new Date(this.dateOfBirth);
    const age = BaseUserProfile.getAge(dateOfBirth);
    const hrMax = BaseUserProfile.calculateHeartRateMax(age);
    this.heartRateMax = Math.round(hrMax);
  }
}
